import { SeverityLevel } from '@sentry/react';
import logLevel from 'loglevel';
import moment from 'moment';
import {
    DBServiceLogger,
    DBWriterLoggerFunctionParams,
} from 'shared/types/logger';

import Sentry from '@/services/sentry';

const logTimestampFormat = 'YYYY-MM-DD HH:mm:ss';
const sentryLevels: Array<SeverityLevel | 'sentryInfo'> = [
    'error',
    'sentryInfo',
    'warning',
    'fatal',
];

function getMessage(message: unknown): string {
    if (typeof message === 'string') {
        return message;
    }

    if (message instanceof Error) {
        return `${message.name}: ${message.message}`;
    }

    try {
        return JSON.stringify(message);
    } catch {
        return String(message);
    }
}

const originalFactory = logLevel.methodFactory;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(logLevel.methodFactory as any) = function (methodName, logLevel, loggerName) {
    const rawMethod = originalFactory(methodName, logLevel, loggerName);

    return function ({
        message,
        modelName,
        messageLevel,
        payload,
    }: {
        message: unknown;
        messageLevel: SeverityLevel | 'sentryInfo';
        modelName?: string;
        payload?: unknown;
    }) {
        const jsonPayload = payload ? JSON.stringify(payload, null, '\t') : '';
        const jsonPayloadSubstring = jsonPayload ? `\n${jsonPayload}` : '';

        const modelNameSubstring = modelName ? `${modelName} | ` : '';

        const timestamp = moment().format(logTimestampFormat);
        const sentryMessage = `${modelNameSubstring}${getMessage(message)}`;
        const sentryLevel =
            messageLevel === 'sentryInfo' ? 'info' : messageLevel;
        const sentryExtras = {
            messageLevel,
            modelName,
            timestamp,
        };
        const sentryFingerprint = [
            'app-logger',
            sentryLevel,
            modelName || 'app',
        ];

        const logString = `${timestamp} | ${messageLevel.toUpperCase()} | ${sentryMessage}${jsonPayloadSubstring}`;

        if (process.env.__DEV__) {
            rawMethod(logString);
        } else if (sentryLevels.includes(messageLevel)) {
            if (Sentry.shouldCaptureException(sentryLevel)) {
                Sentry.recordError(sentryMessage, sentryLevel, {
                    extras: sentryExtras,
                    fingerprint: sentryFingerprint,
                });
                return;
            }

            Sentry.recordMessage(sentryMessage, sentryLevel, {
                extras: sentryExtras,
                fingerprint: sentryFingerprint,
            });
        }
    };
};

logLevel.setLevel(
    process.env.__DEV__ ? logLevel.levels.DEBUG : logLevel.levels.INFO,
);

function logRecordActivity({
    message,
    modelName,
    payload,
}: DBWriterLoggerFunctionParams) {
    let _payload;

    if (typeof payload === 'string') {
        _payload = payload;
    } else if (Array.isArray(payload)) {
        _payload = payload.map((m) => m._raw);
    } else {
        _payload = payload._raw;
    }
    logLevel.info({
        message,
        modelName,
        payload: _payload,
        messageLevel: 'info',
    });
}

function logInfo(message: unknown) {
    logLevel.info({ message, messageLevel: 'info' });
}

function logSentryInfo(message: unknown) {
    logLevel.info({ message, messageLevel: 'sentryInfo' });
}

function logWarn(message: unknown) {
    logLevel.warn({ message, messageLevel: 'warning' });
}

function logError(message: unknown) {
    logLevel.error({ message, messageLevel: 'error' });
}

export const createLoggerFor = (source: string) => ({
    error: (message: string, data?: unknown) =>
        logError(
            data
                ? `${source}: ${message} ${JSON.stringify(data)}`
                : `${source}: ${message}`,
        ),
    warn: (message: string, data?: unknown) =>
        logLevel.warn({
            message: data
                ? `${source}: ${message} ${JSON.stringify(data)}`
                : `${source}: ${message}`,
            messageLevel: 'warning',
        }),
    info: (message: string, data?: unknown) =>
        logInfo(
            data
                ? `${source}: ${message} ${JSON.stringify(data)}`
                : `${source}: ${message}`,
        ),
});

export const dbServiceLogger: DBServiceLogger = {
    error: logError,
    warn: logWarn,
    logDBAction: logRecordActivity,
};

export default {
    logInfo,
    logWarn,
    logError,
    logRecordActivity,
    logSentryInfo,
    createLoggerFor,
};
